import { Controller } from "@hotwired/stimulus"
import { smoothMapOptions, enableSmoothMapZoom } from "../map_helpers"

export default class extends Controller {
  static targets = ["map", "markersData"]
  static values = {
    lat: Number,
    lng: Number,
    radius: { type: Number, default: 500 },
    zoom: { type: Number, default: 12 },
    markers: Array,
    searchAsMove: { type: Boolean, default: true },
    fitMarkers: { type: Boolean, default: true }
  }

  connect() {
    this.splitViewMediaQuery = window.matchMedia("(min-width: 1024px)")
    this.onSplitViewChange = () => {
      if (!this.splitViewMediaQuery.matches) this.clearResultCardHighlight()
    }
    this.splitViewMediaQuery.addEventListener("change", this.onSplitViewChange)
    this.initMap()
  }

  disconnect() {
    this.splitViewMediaQuery?.removeEventListener("change", this.onSplitViewChange)
  }

  mapResultsSyncEnabled() {
    return this.splitViewMediaQuery?.matches ?? window.matchMedia("(min-width: 1024px)").matches
  }

  initMap() {
    if (!this.hasMapTarget) {
      return
    }

    if (typeof L === 'undefined') {
      setTimeout(() => this.initMap(), 100)
      return
    }

    if (this.map) return;

    const urlParams = new URLSearchParams(window.location.search);
    const startLat = parseFloat(urlParams.get('lat')) || this.latValue || 51.505;
    const startLng = parseFloat(urlParams.get('lng')) || this.lngValue || -0.09;
    const startZoom = parseInt(urlParams.get('zoom')) || this.zoomValue || 12;

    this.map = L.map(this.mapTarget, {
      zoomControl: true,
      dragging: false,
      touchZoom: false,
      ...smoothMapOptions()
    }).setView([startLat, startLng], startZoom);

    this.map.on('click', () => {
      this.activateInteractions();
      this.collapseExpandedMarker();
      this.clearResultCardHighlight();
    });

    this.map.on('zoomstart', () => {
      if (this.programmaticMapUpdate) return
      this.activateInteractions();
      this.collapseExpandedMarker();
      this.clearResultCardHighlight();
    });

    this.map.on('moveend', () => {
      if (this.searchAsMoveValue && !this.isOpeningPopup && !this.programmaticMapUpdate) {
        this.scheduleSearchFromMap();
      }
      if (!this.programmaticMapUpdate) {
        this.isOpeningPopup = false;
      }
      if (this.popupResetTimeout) clearTimeout(this.popupResetTimeout);
    });

    this.addInteractionOverlay();

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map);

    if (this.hasMarkersValue && this.markersValue.length > 0) {
      this.addMarkers(this.markersValue)
      if (this.fitMarkersValue && !urlParams.has("lat") && !urlParams.has("lng")) {
        this.fitToMarkers()
      }
    } else if (this.latValue && this.lngValue && this.latValue !== 0) {
      this.addLocationPin()
    }

    setTimeout(() => {
      if (this.map) {
        this.withProgrammaticMapUpdate(() => this.map.invalidateSize())
      }
    }, 250);
  }

  withProgrammaticMapUpdate(callback) {
    this.programmaticMapUpdate = true
    callback()
    this.map.once("moveend", () => {
      this.programmaticMapUpdate = false
    })
  }

  scheduleSearchFromMap() {
    if (!this.interactionsActive || this.programmaticMapUpdate) return

    if (this.mapSearchDebounce) clearTimeout(this.mapSearchDebounce)
    this.mapSearchDebounce = setTimeout(() => {
      this.mapSearchDebounce = null
      this.updateSearchFromMap()
    }, 180)
  }

  addInteractionOverlay() {
    if (this.interactionsActive) return;

    const overlay = document.createElement('div');
    overlay.className = 'absolute inset-0 z-[1000] flex items-center justify-center bg-black/5 pointer-events-auto cursor-pointer transition-opacity duration-300';
    overlay.setAttribute("role", "button");
    overlay.setAttribute("aria-label", "Click to interact with map");
    overlay.addEventListener("click", (event) => {
      event.preventDefault();
      this.activateInteractions();
    });
    overlay.innerHTML = `
      <div class="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-100 flex items-center gap-2">
        <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"></path></svg>
        <span class="text-[10px] font-black uppercase tracking-widest text-gray-900">Click to interact</span>
      </div>
    `;
    this.mapTarget.style.position = 'relative';
    this.mapTarget.appendChild(overlay);
    this.overlay = overlay;
  }

  activateInteractions() {
    if (!this.map || this.interactionsActive) return;

    this.map.dragging.enable();
    this.map.touchZoom.enable();
    enableSmoothMapZoom(this.map);
    this.map.doubleClickZoom.enable();

    this.interactionsActive = true;
    this.mapTarget.classList.add('interactions-active');

    if (this.overlay) {
      this.overlay.classList.add('opacity-0');
      setTimeout(() => this.overlay.remove(), 300);
    }

    this.mapTarget.style.cursor = 'grab';
  }

  refreshMarkers() {
    if (!this.map || !this.hasMarkersDataTarget) return;
    this.collapseExpandedMarker()
    this.clearResultCardHighlight()
    try {
      const newMarkers = JSON.parse(this.markersDataTarget.textContent);
      this.addMarkers(newMarkers);
    } catch (e) {
      console.error("Error parsing markers data:", e);
    }
    this.scrollResultsToTop()
  }

  scrollResultsToTop() {
    const frame = document.getElementById("results-list")
    if (!frame || frame.dataset.resetScrollOnRender !== "true") return
    delete frame.dataset.resetScrollOnRender

    if (!window.matchMedia("(min-width: 1024px)").matches) return
    if (!this.hasMapTarget) return

    const anchor = frame.previousElementSibling || frame
    const navOffset = 112
    const top = anchor.getBoundingClientRect().top + window.scrollY - navOffset
    window.scrollTo({ top: Math.max(0, top), behavior: "auto" })
  }

  fitToMarkers() {
    if (!this.mapMarkers || this.mapMarkers.length === 0) return;
    const bounds = L.latLngBounds(this.mapMarkers.map(m => m.getLatLng()));
    this.programmaticMapUpdate = true;
    this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    this.map.once("moveend", () => {
      this.programmaticMapUpdate = false;
    });
  }

  addLocationPin() {
    if (this.locationPin) this.map.removeLayer(this.locationPin)
    this.locationPin = L.marker([this.latValue, this.lngValue]).addTo(this.map)
  }

  addMarkers(markersArray) {
    this.currentMarkers = markersArray || []

    if (this.mapMarkers) {
      this.mapMarkers.forEach((m) => this.map.removeLayer(m))
    }
    this.mapMarkers = []

    const color = this.markerColor()

    markersArray.forEach((markerData) => {
      if (!markerData.lat || !markerData.lng) return

      const key = this.markerKey(markerData)
      const isExpanded = this.activeMarkerKey === key
      const icon = this.createPriceIcon(markerData, color, isExpanded)

      const marker = L.marker([markerData.lat, markerData.lng], { icon, zIndexOffset: isExpanded ? 1000 : 0 }).addTo(this.map)
      marker._markerKey = key
      marker._markerData = markerData

      marker.on("click", (e) => {
        L.DomEvent.stopPropagation(e)

        const target = e.originalEvent?.target
        if (target?.closest?.(".map-price-tag__btn")) return

        if (this.activeMarkerKey === key) {
          this.collapseExpandedMarker()
          this.clearResultCardHighlight()
          return
        }

        this.activeMarkerKey = key
        this.expandMarkerTag(marker)
        if (markerData.id) this.highlightResultCard(markerData.id)
      })

      this.mapMarkers.push(marker)
    })
  }

  markerColor() {
    return this.element.dataset.mapDisplayColorValue === "green" ? "green" : "blue"
  }

  markerKey(markerData) {
    return markerData.id ? String(markerData.id) : `${markerData.lat},${markerData.lng}`
  }

  highlightResultCard(serviceId) {
    if (!this.mapResultsSyncEnabled()) return

    this.clearResultCardHighlight()

    const card = document.querySelector(`[data-browse-map-result-id="${serviceId}"]`)
    if (!card) return

    card.classList.add("service-card--map-highlight")
    this.highlightedResultCard = card

    card.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
  }

  clearResultCardHighlight() {
    document.querySelectorAll(".service-card--map-highlight").forEach((card) => {
      card.classList.remove("service-card--map-highlight")
    })
    this.highlightedResultCard = null
  }

  collapseAllMarkerTags() {
    if (!this.mapMarkers) return

    this.mapMarkers.forEach((marker) => {
      const tag = marker.getElement()?.querySelector(".map-price-tag")
      if (tag) tag.classList.remove("map-price-tag--expanded")
      marker.setZIndexOffset(0)
    })
  }

  collapseExpandedMarker() {
    if (!this.activeMarkerKey) return
    this.activeMarkerKey = null
    this.collapseAllMarkerTags()
  }

  expandMarkerTag(marker) {
    this.collapseAllMarkerTags()

    const tag = marker.getElement()?.querySelector(".map-price-tag")
    if (!tag) return

    requestAnimationFrame(() => {
      tag.classList.add("map-price-tag--expanded")
      marker.setZIndexOffset(1000)

      const panWhenSettled = () => this.ensureExpandedTagVisible(marker)
      tag.addEventListener("transitionend", (event) => {
        if (event.target.classList.contains("map-price-tag__panel-inner")) {
          panWhenSettled()
        }
      }, { once: true })
      window.setTimeout(panWhenSettled, 380)
    })
  }

  ensureExpandedTagVisible(marker) {
    if (!this.map) return

    const tag = marker.getElement()?.querySelector(".map-price-tag")
    if (!tag?.classList.contains("map-price-tag--expanded")) return

    const mapRect = this.map.getContainer().getBoundingClientRect()
    const tagRect = tag.getBoundingClientRect()
    const pad = 14
    let dx = 0
    let dy = 0

    if (tagRect.top < mapRect.top + pad) {
      dy = tagRect.top - mapRect.top - pad
    } else if (tagRect.bottom > mapRect.bottom - pad) {
      dy = tagRect.bottom - mapRect.bottom + pad
    }

    if (tagRect.left < mapRect.left + pad) {
      dx = tagRect.left - mapRect.left - pad
    } else if (tagRect.right > mapRect.right - pad) {
      dx = tagRect.right - mapRect.right + pad
    }

    if (dx === 0 && dy === 0) return

    this.programmaticMapUpdate = true
    this.isOpeningPopup = true
    this.map.panBy([dx, dy], { animate: true, duration: 0.25 })

    this.map.once("moveend", () => {
      window.setTimeout(() => {
        this.programmaticMapUpdate = false
        this.isOpeningPopup = false
      }, 100)
    })
  }

  createPriceIcon(markerData, color, isExpanded) {
    const price = this.escapeHtml(markerData.price || markerData.label || "")
    const colorClass = color === "green" ? "map-price-tag--green" : "map-price-tag--blue"
    const expandedClass = isExpanded ? " map-price-tag--expanded" : ""
    const btnClass = color === "green" ? "map-price-tag__btn--green" : "map-price-tag__btn--blue"
    const title = this.escapeHtml(markerData.title || "")
    const url = this.escapeHtml(markerData.url || "#")
    const imageAlt = this.escapeHtml(markerData.title || markerData.name || "")
    const image = markerData.image_url
      ? `<div class="map-price-tag__image" style="background-image:url('${this.escapeHtml(markerData.image_url)}')" role="img" aria-label="${imageAlt}"></div>`
      : ""

    const html = `
      <div class="map-price-tag ${colorClass}${expandedClass}" role="button" tabindex="0" aria-expanded="${isExpanded}">
        <div class="map-price-tag__card">
          <div class="map-price-tag__card-inner">
            ${image}
            <div class="map-price-tag__card-body">
              <h4 class="map-price-tag__title">${title}</h4>
              <div class="map-price-tag__footer">
                <span class="map-price-tag__price">${price}</span>
                <a href="${url}" data-turbo-frame="_top" class="map-price-tag__btn ${btnClass}">View</a>
              </div>
            </div>
          </div>
        </div>
        <span class="map-price-tag__chip"><span class="map-price-tag__text">${price}</span></span>
      </div>
    `

    return L.divIcon({
      className: "map-price-marker",
      html,
      iconSize: [0, 0],
      iconAnchor: [0, 0]
    })
  }

  escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }

  async reverseGeocodeLabel(lat, lng) {
    if (this.geocodeAbort) this.geocodeAbort.abort()
    this.geocodeAbort = new AbortController()

    try {
      const q = `format=json&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&addressdetails=1`
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?${q}`, {
        signal: this.geocodeAbort.signal,
        headers: { "Accept-Language": "en" }
      })
      if (!res.ok) return ""
      const data = await res.json()
      const a = data.address || {}
      const place = a.city || a.town || a.village || a.hamlet || a.suburb || a.city_district
      const country = a.country
      if (place && country) return `${place}, ${country}`
      if (data.display_name) {
        return data.display_name.split(",").slice(0, 2).join(",").trim()
      }
      return ""
    } catch (e) {
      if (e.name === "AbortError") return ""
      return ""
    }
  }

  async syncFilterFieldsFromMap(centerLat, centerLng) {
    const queryInput = document.querySelector('input[name="query"]')
    if (queryInput) queryInput.value = ""

    const label = await this.reverseGeocodeLabel(centerLat, centerLng)
    const locationInput = document.querySelector('input[name="location"]')
    if (locationInput) {
      locationInput.value = label || ""
    }
  }

  async updateSearchFromMap() {
    if (!this.map || !this.hasMapTarget || !this.interactionsActive) return;
    const center = this.map.getCenter();
    const zoom = this.map.getZoom();
    const bounds = this.map.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const boundsFp = `${sw.lat.toFixed(5)},${sw.lng.toFixed(5)},${ne.lat.toFixed(5)},${ne.lng.toFixed(5)},${zoom}`
    if (this.lastBoundsFp === boundsFp) return
    this.lastBoundsFp = boundsFp

    const url = new URL(window.location.href);
    url.searchParams.delete("query")
    url.searchParams.delete("location")
    url.searchParams.delete("country")
    url.searchParams.delete("page")

    url.searchParams.set('min_lat', sw.lat);
    url.searchParams.set('max_lat', ne.lat);
    url.searchParams.set('min_lng', sw.lng);
    url.searchParams.set('max_lng', ne.lng);
    url.searchParams.set('lat', center.lat.toFixed(6));
    url.searchParams.set('lng', center.lng.toFixed(6));
    url.searchParams.set('zoom', zoom);

    await this.syncFilterFieldsFromMap(center.lat, center.lng)

    const currentState = window.history.state || {}
    window.history.replaceState(currentState, "", url.toString())

    const frame = document.getElementById('results-list');
    if (frame) {
      frame.dataset.resetScrollOnRender = "true"
      frame.src = url.toString();
    }
  }

  disconnect() {
    if (this.mapSearchDebounce) clearTimeout(this.mapSearchDebounce)
    if (this.geocodeAbort) this.geocodeAbort.abort()
    if (this.map) this.map.remove()
  }
}
